<template>
  <td 
    class="editableCell text-right"
    :class="{ 'cellFocused': isFocused && !invalid && !isEditing, 
      'cellFocusedInvalid': isFocused && invalid && !isEditing, 
      'cellInvalid': invalid && !isFocused && !isEditing }"
    @click="setFocus"
  >
    <div class="cellWrapper">
      <!-- Editing mode -->
      <div
        v-if="isEditing"
        class="cellContent"
        :class="{ 'cellContentInvalid': invalid, 'cellEditing': !invalid }"
        contenteditable="true"
        ref="inputRef"
        @keydown="handleKeydown"
        @blur="finishEdit"
      >
        {{ value }}
      </div>

      <!-- Display mode -->
      <span 
        v-else 
        class="cellDisplay"
        :class="{ 'nonEditable': !editable }"
        @dblclick="requestEdit"
        @keydown="handleDisplayKeydown"
        tabindex="0"
        ref="displayRef"
      >
        {{ valueFormatted }}
      </span>

      <!-- Search icon -->
      <i v-if="showSearchIcon && isEditing" class="fas fa-search searchIcon"></i>
    </div>
  </td>
</template>

<script>
import { EventBus } from '@/event-bus.js';
import validationMixin from '@/common/validationMixin.js';
import ShowMessage from '@/components/messages/ShowMessage.js';

export default {
  name: 'EditableInputCell',
  mixins: [validationMixin, ShowMessage],
  props: {
    name: { type: String, required: true },
    value: [String, Number],
    showSearchIcon: { type: Boolean, default: false },
    formatter: { type: Function, default: null },
    editable: { type: Boolean, default: true },
    isFocused: { type: Boolean, default: false },
    validatorRules: { type: Array, default: () => [] },
  },
  data() {
    return {
      isEditing: false,
      invalid: false
    };
  },
  computed: {
    valueFormatted() {
      return this.formatter ? this.formatter(this.value) : this.value;
    },
    row() {
      const split = this.name.split('-');
      return split.length === 3 ? parseInt(split[1]) : -1;
    }
  },
  methods: {
    setFocus() {
      this.$emit('cellFocus', this.row, this.name);
    },
    requestEdit() {
      if (!this.editable) return;
      this.$emit('requestEdit', { row: this.row, name: this.name });
    },
    startEdit() {
      if (!this.editable) {
        this.$nextTick(() => this.$refs.displayRef.focus());
        return;
      }
      
      this.isEditing = true;
      this.$nextTick(() => {
        const el = this.$refs.inputRef;
        el.focus();

        // Seleccionar todo el contenido
        const range = document.createRange();
        range.selectNodeContents(el);

        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      });
    },
    finishEdit(event) {
      const val = event.target.innerText.trim();
      const changed = val !== String(this.value).trim();

      // Solo emitir si cambió respecto al prop value
      if (changed) {
        this.$emit('input', val);
      }

      const valid = this.validateCell(val);

      if (valid) {
        this.isEditing = false;
        
        if (changed) {
          this.emitChanged(val);
        }
      } else {
        this.isEditing = true;
        //this.$emit('cellFocus', this.row, this.name);
        //this.$nextTick(() => this.$refs.inputRef.focus());
        this.showMessage(this.$tc('Msg.Error'), this.invalidMessage(this.name), "errorAutoHide");
      }
    },

    handleKeydown(event) { 
      switch (event.key) { 
        case 'Enter': 
          event.preventDefault(); 
          this.finishEdit(event); 

          if (!this.invalid) this.$emit('nextCell'); 
          break; 
        case 'Tab': 
          event.preventDefault(); 
          this.finishEdit(event); 

          if (!this.invalid) { 
            this.$emit(event.shiftKey ? 'prevCell' : 'nextCell'); 
          } 
          break; 
        case 'ArrowUp': 
          if (this.caretAtStart()) { 
            event.preventDefault(); 
            this.finishEdit(event); 

            if (!this.invalid) this.$emit('upCell'); 
          } 
          break; 
        case 'ArrowDown': 
          if (this.caretAtEnd()) { 
            event.preventDefault(); 
            this.finishEdit(event); 

            if (!this.invalid) this.$emit('downCell'); 
          } 
          break; 
        case 'ArrowLeft': 
          if (this.caretAtStart()) { 
            event.preventDefault(); 
            this.finishEdit(event); 

            if (!this.invalid) this.$emit('prevCell'); 
          } 
          break; 
        case 'ArrowRight': 
          if (this.caretAtEnd()) { 
            event.preventDefault(); 
            this.finishEdit(event); 

            if (!this.invalid) this.$emit('nextCell'); 
          } break; 
      } 
    },

    handleDisplayKeydown(event) {
      switch (event.key) {
        case 'Enter':
          event.preventDefault();

          if (this.editable) {
            this.requestEdit();
          } else {
            this.$emit('nextCell'); 
          }
          break;

        case 'F2': // opcional, estilo Excel
          if (this.editable) {
            event.preventDefault();
            this.requestEdit();
          }
          break;

        case 'Tab':
          event.preventDefault();
          this.$emit(event.shiftKey ? 'prevCell' : 'nextCell');
          break;

        case 'ArrowUp':
          event.preventDefault();
          this.$emit('upCell');
          break;

        case 'ArrowDown':
          event.preventDefault();
          this.$emit('downCell');
          break;

        case 'ArrowLeft':
          event.preventDefault();
          this.$emit('prevCell');
          break;

        case 'ArrowRight':
          event.preventDefault();
          this.$emit('nextCell');
          break;
      }
    },

    caretAtStart() {
      const sel = window.getSelection();
      return sel.anchorOffset === 0;
    },

    caretAtEnd() {
      const sel = window.getSelection();
      const el = this.$refs.inputRef;
      return sel.anchorOffset === el.innerText.length;
    },

    emitChanged(value) {
      EventBus.$emit('onChanged', { Changed: true, componentName: this.name, value });
    },
    validateCell(val = null) {
      let state = true;
      const valueToCheck = val !== null ? val : String(this.value ?? '').trim();

      if (this.validatorRules) {
        const rules = {
          [this.name]: this.validatorRules
        };
        state = this.validateFields(rules, { [this.name]: valueToCheck });
      }

      this.invalid = !state;
      if (this.isFocused) {
        this.$emit('validationState', { name: this.name, invalid: this.invalid });
      }

      return state;
    },
  }
};
</script>

<style scoped>
.editableCell { padding: 0 !important; min-width: 120px; }
.cellFocused { box-shadow: inset 0 0 0 1px #007bff !important; }
.cellWrapper { position: relative; display: flex; align-items: center; width: 100%; height: 100%; }
.cellDisplay { flex: 1; padding: 8px; cursor: pointer; }
.nonEditable { cursor: default; color: #555; }
.cellEditing { flex: 1; padding: 6px; outline: none; width: calc(100% - 6px); margin: 2px;
  border: 2px solid #007bff; border-radius: 4px; background-color: #fff; 
  cursor: text;}
.hasSearch .cellContent, .hasSearch .cellDisplay { padding-right: 30px; }
.searchIcon { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); color: #007bff; pointer-events: none; }
.cellContentInvalid { flex: 1; padding: 6px; outline: none; width: calc(100% - 6px); margin: 2px;
  border: 2px solid red; border-radius: 4px; background-color: #fff; }
.cellInvalid { background-color: #fae3de !important; box-shadow: inset 0 0 0 1px red !important; }
.cellFocusedInvalid { background-color: #fae3de !important; box-shadow: inset 0 0 0 1px #007bff !important; }
</style>
